import React, { useEffect } from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertColor, AlertProps } from '@mui/material/Alert';
import { Box, Typography, Collapse, IconButton, Divider, Button } from '@mui/material';
import { ExpandMore, ExpandLess, ContentCopy } from '@mui/icons-material';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export interface IErrorDetails {
    traceId?: string;
    code?: string;
    detail?: string;
}

export interface DefaultMessageProps {
    message?: string;
    open?: boolean;
    onClose?: () => void;
    type?: AlertColor;
    errorDetails?: IErrorDetails;
}


export default function DefaultMessage(props: DefaultMessageProps) {

    const [defaultMessage, setDefaultMessage] = React.useState<DefaultMessageProps>(props);
    const [expanded, setExpanded] = React.useState(false);

    useEffect(() => {
        if (props.open !== undefined)
            setDefaultMessage(props);
        // Reset expanded state when message changes
        setExpanded(false);
    }, [props]);

    const handleClose = () => {
        if (props.onClose !== undefined) {
            props.onClose();
        }
    };

    const hasErrorDetails = defaultMessage.errorDetails && (
        defaultMessage.errorDetails.traceId ||
        defaultMessage.errorDetails.code ||
        defaultMessage.errorDetails.detail
    );

    const handleToggleExpand = (e: React.MouseEvent) => {
        e.stopPropagation();
        setExpanded(!expanded);
    };

    const handleCopyDetails = () => {
        const details = `
Trace ID: ${defaultMessage.errorDetails?.traceId || 'N/A'}
Código: ${defaultMessage.errorDetails?.code || 'N/A'}
Detalhes: ${defaultMessage.errorDetails?.detail || 'N/A'}
Mensagem: ${defaultMessage.message}
        `.trim();

        navigator.clipboard.writeText(details).catch((err) => {
            console.error('Falha ao copiar detalhes:', err);
        });
    };

    return (
        <Snackbar
            open={defaultMessage.open}
            autoHideDuration={6000}
            onClose={handleClose}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "right"
            }}
        >
            <Alert
                onClose={handleClose}
                severity={defaultMessage.type !== undefined ? defaultMessage.type : "success"}
                variant="filled"
                sx={{
                    width: '100%',
                    whiteSpace: 'pre-line',
                    minWidth: '350px',
                    maxWidth: '500px',
                    border: '1px solid',
                    borderColor: 'transparent',
                    borderRadius: '4px',
                    boxShadow: 'none',
                    '& .MuiAlert-icon': {
                        fontSize: '20px',
                        alignItems: 'flex-start',
                        paddingTop: '8px'
                    },
                    '& .MuiAlert-message': {
                        width: '100%',
                        padding: '0'
                    },
                    '& .MuiAlert-action': {
                        alignItems: 'flex-start',
                        paddingTop: '4px',
                        marginRight: 0
                    }
                }}
            >
                <Box display="flex" flexDirection="column" width="100%">
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.5, py: '4px' }}>
                            {defaultMessage.message}
                        </Typography>
                        {hasErrorDetails && (
                            <IconButton
                                size="small"
                                onClick={handleToggleExpand}
                                aria-label="Mostrar detalhes do erro"
                                sx={{
                                    ml: 1,
                                    p: '4px',
                                    color: 'inherit',
                                    opacity: 0.9,
                                    '&:hover': {
                                        opacity: 1,
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </IconButton>
                        )}
                    </Box>

                    {hasErrorDetails && (
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Divider
                            sx={{
                                my: 1.5,
                                backgroundColor: 'rgba(255, 255, 255, 0.2)'
                            }}
                        />
                        <Box
                            sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                borderRadius: '4px',
                                padding: 1.5,
                                fontSize: '0.813rem'
                            }}
                        >
                            {defaultMessage.errorDetails?.traceId && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.813rem',
                                            opacity: 0.9
                                        }}
                                    >
                                        Trace ID:{' '}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            wordBreak: 'break-all',
                                            opacity: 0.85
                                        }}
                                    >
                                        {defaultMessage.errorDetails.traceId}
                                    </Typography>
                                </Box>
                            )}
                            {defaultMessage.errorDetails?.code && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.813rem',
                                            opacity: 0.9
                                        }}
                                    >
                                        Código:{' '}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '0.813rem',
                                            opacity: 0.85
                                        }}
                                    >
                                        {defaultMessage.errorDetails.code}
                                    </Typography>
                                </Box>
                            )}
                            {defaultMessage.errorDetails?.detail && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.813rem',
                                            opacity: 0.9
                                        }}
                                    >
                                        Detalhes:{' '}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '0.813rem',
                                            opacity: 0.85
                                        }}
                                    >
                                        {defaultMessage.errorDetails.detail}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    onClick={handleCopyDetails}
                                    startIcon={<ContentCopy sx={{ fontSize: '16px' }} />}
                                    sx={{
                                        fontSize: '0.813rem',
                                        fontWeight: 600,
                                        padding: '4px 12px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                        color: 'inherit',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                            border: '1px solid rgba(255, 255, 255, 0.3)'
                                        }
                                    }}
                                >
                                    Copiar Detalhes
                                </Button>
                            </Box>
                        </Box>
                    </Collapse>
                )}
                </Box>
            </Alert>
        </Snackbar>
    );
}
